import React, { FC, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { loadPdf, LoadedPdf } from '../../../utility/pdfViewer';

const SCALE = 1.2;

export interface PdfPageViewProps {
  url: string;
  pageNumber: number;
  // Called with the click position in pixels from the top-left of the rendered page.
  onPageClick?: (clickX: number, clickY: number, scale: number) => void;
  // Markers drawn on top of the page (positions in rendered pixels).
  children?: (scale: number) => ReactNode;
  onLoaded?: (pageCount: number) => void;
}

// Draws one page of a PDF and reports clicks on it. Used by the click-to-place editor.
const PdfPageView: FC<PdfPageViewProps> = ({ url, pageNumber, onPageClick, children, onLoaded }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ pdf, setPdf ] = useState<LoadedPdf | null>(null);
  const [ error, setError ] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loaded: LoadedPdf | null = null;
    loadPdf(url)
      .then((doc) => {
        if (cancelled) { doc.destroy(); return; }
        loaded = doc;
        setPdf(doc);
        onLoaded?.(doc.pageCount);
      })
      .catch(() => setError(true));
    return () => { cancelled = true; loaded?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ url ]);

  useEffect(() => {
    if (pdf && canvasRef.current) {
      pdf.renderPage(pageNumber, canvasRef.current, SCALE).catch(() => setError(true));
    }
  }, [ pdf, pageNumber ]);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    onPageClick?.(e.clientX - rect.left, e.clientY - rect.top, SCALE);
  };

  if (error) return <p className="text-sm text-red-600">We could not show this PDF. Please try again.</p>;

  return (
    <div className="relative inline-block border border-gray-300" onClick={handleClick} style={{ cursor: onPageClick ? 'crosshair' : 'default' }}>
      <canvas ref={canvasRef} />
      {children?.(SCALE)}
    </div>
  );
};

export default PdfPageView;
